import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { Pose } from '@mediapipe/pose';
import { drawConnectors, drawLandmarks } from '@mediapipe/drawing_utils';
import { POSE_CONNECTIONS } from '@mediapipe/pose';
import { Play, Pause, CheckCircle, Download, Share2, Scissors, RotateCcw, Box, Settings, Activity, ChevronDown, ChevronUp } from 'lucide-react';
import { calculateAngle } from '../../utils/geometry';
import { EMASmoother } from '../../utils/smoothing';
import Biomechanics3DView from './Biomechanics3DView';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
);

const ANGLE_DEFINITIONS = {
    elbow_left: { label: "Left Elbow", indices: [11, 13, 15], color: "#00FFFF" },
    elbow_right: { label: "Right Elbow", indices: [12, 14, 16], color: "#FF00FF" },
    knee_left: { label: "Left Knee", indices: [23, 25, 27], color: "#00FF00" },
    knee_right: { label: "Right Knee", indices: [24, 26, 28], color: "#FFFF00" },
    shoulder_left: { label: "Left Shoulder", indices: [13, 11, 23], color: "#FF8000" },
    shoulder_right: { label: "Right Shoulder", indices: [14, 12, 24], color: "#FF0080" },
    hip_left: { label: "Left Hip", indices: [11, 23, 25], color: "#80FF00" },
    hip_right: { label: "Right Hip", indices: [12, 24, 26], color: "#8000FF" }
};

const PoseAnalyzer = ({ videoSrc, role, onRestart }) => {
    const videoRef = useRef(null);
    const canvasRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isModelLoading, setIsModelLoading] = useState(true);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [keyFrame, setKeyFrame] = useState(null); // Time of contact/release
    const [trimRange, setTrimRange] = useState(null); // { start, end }
    const [isExporting, setIsExporting] = useState(false);
    const [show3D, setShow3D] = useState(true);
    const [landmarks3D, setLandmarks3D] = useState(null);
    const [shots, setShots] = useState([]); // Array of { timestamp, score }
    const angleHistoryRef = useRef([]); // Buffer for detecting changes

    // Configuration & Interaction State
    const [sensitivity, setSensitivity] = useState(5);
    const [smoothingWindow, setSmoothingWindow] = useState(0.25); // seconds
    const [graphData, setGraphData] = useState({
        labels: [],
        instant: [],
        average: [],
        threshold: []
    });
    const graphBufferRef = useRef([]); // { t, instant, average }
    const [armPath, setArmPath] = useState([]); // Array of {x, y, v, t}
    const pathBufferRef = useRef([]); // High-frequency buffer for drawing
    const [releaseSpeed, setReleaseSpeed] = useState(0);
    const releaseSpeedRef = useRef(0); // Ref for canvas drawing to avoid stale closure
    const [bowlingArm, setBowlingArm] = useState('right'); // 'left' or 'right'

    const [overlayConfig, setOverlayConfig] = useState({
        showArmPath: true,
        showStats: true,
        showSkeleton: true,
        activeAngles: role === 'bowler' ? ['knee_right', 'elbow_right'] : ['elbow_left', 'knee_left'],
        externalAngles: {} // Map of angleId -> boolean
    });

    const poseRef = useRef(null);
    const mediaRecorderRef = useRef(null);
    const recordedChunksRef = useRef([]);

    // Smoother instances - using alpha 0.5 for balance of smoothness and latency
    const smoother2D = useMemo(() => new EMASmoother(0.5, 1, 0), []);
    const smoother3D = useMemo(() => new EMASmoother(0.5, 1, 0), []);

    useEffect(() => {
        const pose = new Pose({
            locateFile: (file) => {
                // Use jsdelivr to avoid local serving issues
                return `https://cdn.jsdelivr.net/npm/@mediapipe/pose/${file}`;
            }
        });

        pose.setOptions({
            modelComplexity: 1,
            smoothLandmarks: true,
            enableSegmentation: false,
            minDetectionConfidence: 0.5,
            minTrackingConfidence: 0.5
        });

        pose.onResults(onResults);
        poseRef.current = pose;
        setIsModelLoading(false);

        return () => {
            pose.close();
        };
    }, []);



    const onResults = useCallback((results) => {
        if (!canvasRef.current || !videoRef.current) return;

        const ctx = canvasRef.current.getContext('2d');
        const { width, height } = canvasRef.current;

        ctx.save();
        ctx.clearRect(0, 0, width, height);

        // Draw the video frame to the canvas
        if (results.image) {
            ctx.drawImage(results.image, 0, 0, width, height);
        }

        if (results.poseLandmarks) {
            // Apply Smoothing to 2D landmarks (norm coords)
            const smoothed2D = smoother2D.process(results.poseLandmarks);

            // Draw Skeleton with smoothed data
            if (smoothed2D) {
                if (overlayConfig.showSkeleton) {
                    drawConnectors(ctx, smoothed2D, POSE_CONNECTIONS,
                        { color: '#00FF00', lineWidth: 4 });
                    drawLandmarks(ctx, smoothed2D,
                        { color: '#FF0000', lineWidth: 2 });
                }

                if (role === 'bowler' && overlayConfig.showArmPath) {
                    handleBowlerPath(smoothed2D, ctx);
                    drawArmPath(ctx, pathBufferRef.current);
                } else if (role === 'bowler') {
                    // Still need to handle path logic even if not drawing? 
                    // No, usually path is tied to visual. But velocity/release speed logic is in handleBowlerPath.
                    // Let's call it but maybe drawArmPath should check config.
                    handleBowlerPath(smoothed2D, ctx);
                }

                calculateRealTimeStats(smoothed2D, ctx);
            }
        }

        // Update 3D model with smoothed world landmarks
        if (results.poseWorldLandmarks) {
            // MediaPipe world landmarks are also x,y,z,visibility
            const smoothed3D = smoother3D.process(results.poseWorldLandmarks);
            if (smoothed3D) {
                setLandmarks3D(smoothed3D);
            }
        }

        ctx.restore();
    }, [role, smoother2D, smoother3D, sensitivity, smoothingWindow, overlayConfig]);

    // Update onResults callback when it changes
    useEffect(() => {
        if (poseRef.current) {
            poseRef.current.onResults(onResults);
        }
    }, [onResults]);

    const drawAngleArc = (ctx, A, B, C, label, color = 'white', isExternal = false) => {
        if (!A || !B || !C) return;
        const radius = 30;
        const v1 = { x: A.x - B.x, y: A.y - B.y };
        const v2 = { x: C.x - B.x, y: C.y - B.y };
        const angle1 = Math.atan2(v1.y, v1.x);
        const angle2 = Math.atan2(v2.y, v2.x);

        ctx.save();
        ctx.beginPath();
        ctx.moveTo(B.x * ctx.canvas.width, B.y * ctx.canvas.height);
        // If external, we use the other side of the circle
        ctx.arc(B.x * ctx.canvas.width, B.y * ctx.canvas.height, radius, angle1, angle2, isExternal);
        ctx.closePath();
        ctx.fillStyle = color + '4D'; // 30% opacity
        ctx.fill();
        ctx.strokeStyle = color;
        ctx.lineWidth = 2;
        ctx.stroke();

        let angleVal = calculateAngle(A, B, C);
        if (isExternal) angleVal = 360 - angleVal;

        ctx.fillStyle = 'white';
        ctx.font = 'bold 12px monospace';
        ctx.textAlign = 'center';

        let midAngle = (angle1 + angle2) / 2;
        if (isExternal) midAngle += Math.PI;
        if (Math.abs(angle1 - angle2) > Math.PI && !isExternal) midAngle += Math.PI;

        const txtX = B.x * ctx.canvas.width + (radius + 20) * Math.cos(midAngle);
        const txtY = B.y * ctx.canvas.height + (radius + 20) * Math.sin(midAngle);
        ctx.fillText(`${angleVal}°`, txtX, txtY);
        ctx.restore();
    };

    const drawArmPath = (ctx, path) => {
        if (!path || path.length < 2) return;
        ctx.save();
        const w = ctx.canvas.width;
        const h = ctx.canvas.height;

        // 1. Draw "Spokes" (Shoulder -> Elbow -> Wrist) with low opacity
        const step = Math.max(1, Math.floor(path.length / 40));
        ctx.lineWidth = 1;

        for (let i = 0; i < path.length; i += step) {
            const p = path[i];
            if (!p.shoulder || !p.elbow || !p.wrist) continue;
            const speedFact = Math.min(1, p.v / 15);
            ctx.strokeStyle = `rgba(255, 255, 255, ${0.1 + (speedFact * 0.2)})`;

            ctx.beginPath();
            ctx.moveTo(p.shoulder.x * w, p.shoulder.y * h);
            ctx.lineTo(p.elbow.x * w, p.elbow.y * h);
            ctx.lineTo(p.wrist.x * w, p.wrist.y * h);
            ctx.stroke();

            // Small dot at wrist for each spoke
            ctx.beginPath();
            ctx.arc(p.wrist.x * w, p.wrist.y * h, 1.5, 0, Math.PI * 2);
            ctx.fillStyle = 'rgba(255,255,255,0.4)';
            ctx.fill();
        }

        // 2. Draw smooth Bezier Path for Wrist
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.lineWidth = 12; // Bold, premium look

        if (path.length > 2) {
            // Move to first midpoint
            const startXC = (path[0].wrist.x + path[1].wrist.x) / 2;
            const startYC = (path[0].wrist.y + path[1].wrist.y) / 2;

            for (let i = 1; i < path.length - 1; i++) {
                const p1 = path[i];
                const p2 = path[i + 1];

                const xc = (p1.wrist.x + p2.wrist.x) / 2;
                const yc = (p1.wrist.y + p2.wrist.y) / 2;

                const speedFact = Math.min(1, p1.v / 15);
                const r = Math.floor(255 * speedFact);
                const g = Math.floor(255 * (1 - Math.abs(speedFact - 0.5) * 2));
                const b = Math.floor(255 * (1 - speedFact));

                ctx.beginPath();
                ctx.strokeStyle = `rgb(${r}, ${g}, ${b})`;
                // Move to the midpoint of the previous segment
                const prevXC = i === 1 ? startXC : (path[i - 1].wrist.x + path[i].wrist.x) / 2;
                const prevYC = i === 1 ? startYC : (path[i - 1].wrist.y + path[i].wrist.y) / 2;

                ctx.moveTo(prevXC * w, prevYC * h);
                ctx.quadraticCurveTo(p1.wrist.x * w, p1.wrist.y * h, xc * w, yc * h);
                ctx.stroke();
            }
        }

        // 3. Draw end point (current position)
        const last = path[path.length - 1];
        if (last.wrist) {
            ctx.beginPath();
            ctx.arc(last.wrist.x * w, last.wrist.y * h, 8, 0, Math.PI * 2);
            ctx.fillStyle = 'white';
            ctx.fill();
            ctx.strokeStyle = '#00ffff';
            ctx.lineWidth = 3;
            ctx.stroke();
        }

        ctx.restore();
    };

    const drawStatsOverlay = (ctx, stats) => {
        if (!stats || stats.length === 0) return;

        const rowHeight = 45;
        const padding = 15;
        const boxWidth = 220;
        const boxHeight = (stats.length * rowHeight) + (padding * 2);
        const x = ctx.canvas.width - boxWidth - 20;
        const y = 80;

        ctx.save();
        ctx.fillStyle = 'rgba(0, 0, 0, 0.8)';
        ctx.beginPath();
        ctx.roundRect(x, y, boxWidth, boxHeight, 16);
        ctx.fill();
        ctx.strokeStyle = '#333';
        ctx.lineWidth = 1;
        ctx.stroke();

        ctx.textAlign = 'left';
        ctx.textBaseline = 'top';

        stats.forEach((stat, i) => {
            const rowY = y + padding + (i * rowHeight);

            ctx.font = '10px monospace';
            ctx.fillStyle = '#9ca3af';
            ctx.fillText(stat.label.toUpperCase(), x + padding, rowY);

            ctx.font = 'bold 24px monospace';
            ctx.fillStyle = stat.color || '#00ffff';
            ctx.fillText(`${stat.value.toFixed(0)}°`, x + padding, rowY + 12);
        });

        ctx.restore();
    };

    const calculateRealTimeStats = (landmarks, ctx) => {
        const stats = [];

        overlayConfig.activeAngles.forEach(angleId => {
            const def = ANGLE_DEFINITIONS[angleId];
            if (!def) return;
            const pts = def.indices.map(i => landmarks[i]);
            let angleVal = calculateAngle(pts[0], pts[1], pts[2]);
            const isExternal = overlayConfig.externalAngles[angleId];
            if (isExternal) angleVal = 360 - angleVal;

            stats.push({ label: def.label, value: angleVal, color: def.color });

            if (ctx) {
                drawAngleArc(ctx, pts[0], pts[1], pts[2], angleId, def.color, isExternal);
            }
        });

        if (ctx && overlayConfig.showStats) {
            drawStatsOverlay(ctx, stats);
        }

        if (ctx) {

            // Draw Release Speed Tag if detected
            if (releaseSpeedRef.current > 0) {
                const last = pathBufferRef.current[pathBufferRef.current.length - 1];
                const isKeyFrame = keyFrame && Math.abs(videoRef.current.currentTime - keyFrame) < 0.1;

                ctx.save();
                ctx.fillStyle = isKeyFrame ? 'rgba(0,255,255,0.9)' : 'rgba(0,0,0,0.7)';
                ctx.fillRect(last.wrist.x * ctx.canvas.width + 10, last.wrist.y * ctx.canvas.height - 40, 80, 40);

                ctx.fillStyle = isKeyFrame ? '#000' : '#00ffff';
                ctx.font = 'bold 10px monospace';
                ctx.fillText('RELEASE', last.wrist.x * ctx.canvas.width + 15, last.wrist.y * ctx.canvas.height - 25);
                ctx.font = 'bold 14px monospace';
                ctx.fillText(`${(releaseSpeedRef.current * 3.6).toFixed(0)} km/h`, last.wrist.x * ctx.canvas.width + 15, last.wrist.y * ctx.canvas.height - 10);
                ctx.restore();
            }

            // If at keyFrame, highlight the stats box or show a persistent tag
            if (keyFrame && Math.abs(videoRef.current.currentTime - keyFrame) < 0.05) {
                // Flash a notification or keep stats bold
                ctx.save();
                ctx.strokeStyle = '#00ffff';
                ctx.lineWidth = 4;
                ctx.strokeRect(ctx.canvas.width - 240, 70, 240, 130);
                ctx.restore();
            }
        }

        if (role === 'batsman') {
            if (!videoRef.current || videoRef.current.paused) return;
            const ptsP = [11, 13, 15].map(i => landmarks[i]);
            const ptsS = [23, 25, 27].map(i => landmarks[i]);
            const primaryAngle = calculateAngle(ptsP[0], ptsP[1], ptsP[2]);
            const secondaryAngle = calculateAngle(ptsS[0], ptsS[1], ptsS[2]);

            const timestamp = videoRef.current.currentTime;
            angleHistoryRef.current.push({ t: timestamp, p: primaryAngle, s: secondaryAngle });
            if (angleHistoryRef.current.length > 100) angleHistoryRef.current.shift();

            const windowFrames = Math.max(2, Math.round(smoothingWindow * 60));
            if (angleHistoryRef.current.length < windowFrames) return;

            const recentFn = angleHistoryRef.current.slice(-windowFrames);
            let avgScore = 0;
            for (let i = 2; i < recentFn.length; i++) {
                const dP = Math.abs(recentFn[i].p - recentFn[i - 2].p) / 2;
                const dS = Math.abs(recentFn[i].s - recentFn[i - 2].s) / 2;
                let s = (dP * 2.0) + dS;
                avgScore += s;
            }
            avgScore /= (recentFn.length - 2);

            if (avgScore > sensitivity) {
                setShots(prevShots => {
                    const lastShot = prevShots[prevShots.length - 1];
                    if (!lastShot || (timestamp - lastShot.timestamp > 2.0)) {
                        return [...prevShots, { id: Date.now(), timestamp, score: avgScore }];
                    }
                    return prevShots;
                });
            }

            const last = recentFn.length - 1;
            const diffP = Math.abs(recentFn[last].p - recentFn[last - 2].p) / 2;
            const diffS = Math.abs(recentFn[last].s - recentFn[last - 2].s) / 2;
            const instantScore = (diffP * 2.0 + diffS);

            graphBufferRef.current.push({
                t: timestamp,
                instant: instantScore,
                average: avgScore,
                threshold: sensitivity
            });

            if (graphBufferRef.current.length > 150) graphBufferRef.current.shift();

            if (graphBufferRef.current.length % 5 === 0) {
                setGraphData({
                    labels: graphBufferRef.current.map(d => d.t.toFixed(2)),
                    instant: graphBufferRef.current.map(d => d.instant),
                    average: graphBufferRef.current.map(d => d.average),
                    threshold: graphBufferRef.current.map(d => sensitivity)
                });
            }
        }
    };

    const handleBowlerPath = (landmarks, ctx) => {
        if (!videoRef.current || videoRef.current.paused) return;
        let wristIdx, elbowIdx, shoulderIdx;
        if (bowlingArm === 'right') {
            wristIdx = 16; elbowIdx = 14; shoulderIdx = 12;
        } else {
            wristIdx = 15; elbowIdx = 13; shoulderIdx = 11;
        }

        const activeWrist = landmarks[wristIdx];
        const activeElbow = landmarks[elbowIdx];
        const activeShoulder = landmarks[shoulderIdx];
        const timestamp = videoRef.current.currentTime;

        let velocity = 0;
        if (pathBufferRef.current.length > 0) {
            const last = pathBufferRef.current[pathBufferRef.current.length - 1];
            const dx = activeWrist.x - last.wrist.x;
            const dy = activeWrist.y - last.wrist.y;
            velocity = Math.sqrt(dx * dx + dy * dy) * 1000;
        }

        const point = {
            wrist: { x: activeWrist.x, y: activeWrist.y },
            elbow: { x: activeElbow.x, y: activeElbow.y },
            shoulder: { x: activeShoulder.x, y: activeShoulder.y },
            v: velocity,
            t: timestamp
        };

        pathBufferRef.current.push(point);
        setArmPath([...pathBufferRef.current]);

        if (velocity > releaseSpeedRef.current) {
            releaseSpeedRef.current = velocity;
            setReleaseSpeed(velocity);
        }
    };

    const processFrame = useCallback(async () => {
        if (videoRef.current && !videoRef.current.paused && poseRef.current) {
            await poseRef.current.send({ image: videoRef.current });
            if (isPlaying || isExporting) requestAnimationFrame(processFrame);
        }
    }, [isPlaying, isExporting]);

    useEffect(() => {
        if (isPlaying) {
            processFrame();
        }
    }, [isPlaying, processFrame]);

    const togglePlay = () => {
        if (videoRef.current) {
            if (isPlaying) videoRef.current.pause();
            else videoRef.current.play();
            setIsPlaying(!isPlaying);
        }
    };

    const handleTimeUpdate = () => {
        if (videoRef.current) {
            const t = videoRef.current.currentTime;

            // Detect Seek or Loop (jump backwards or large jump forwards)
            if (Math.abs(t - currentTime) > 0.5 || t < currentTime) {
                smoother2D.reset();
                smoother3D.reset();
                pathBufferRef.current = [];
                releaseSpeedRef.current = 0;
                setArmPath([]);
                setReleaseSpeed(0);
            }

            setCurrentTime(t);
            // Loop logic
            if (trimRange && !isExporting) {
                if (videoRef.current.currentTime >= trimRange.end) {
                    videoRef.current.currentTime = trimRange.start;
                    smoother2D.reset();
                    smoother3D.reset();
                    pathBufferRef.current = [];
                    setArmPath([]);
                }
            }
        }
    };

    const setKeyFrameCurrent = () => {
        const time = videoRef.current ? videoRef.current.currentTime : 0;
        setKeyFrame(time);
        // Mark Release no longer trims the video automatically
    };

    const handleViewShot = (shot) => {
        if (!videoRef.current) return;
        const start = Math.max(0, shot.timestamp - 1.0);
        const end = Math.min(duration, shot.timestamp + 1.0);

        setIsPlaying(false);
        setTrimRange({ start, end });
        videoRef.current.currentTime = start;
        // Auto-play the clip loop
        setTimeout(() => {
            videoRef.current.play();
            setIsPlaying(true);
        }, 100);
    };

    // Keep individual export logic for the "Save" button
    const downloadShot = async (start, end, filename) => {
        return new Promise((resolve) => {
            if (!canvasRef.current || !videoRef.current) {
                resolve();
                return;
            }

            videoRef.current.pause();
            videoRef.current.currentTime = start;

            const stream = canvasRef.current.captureStream(60);

            // Try MP4, fallback to WebM
            let mimeType = 'video/mp4; codecs="avc1.42E01E, mp4a.40.2"';
            if (!MediaRecorder.isTypeSupported(mimeType)) {
                mimeType = 'video/webm; codecs=vp9';
            }

            const options = {
                mimeType,
                videoBitsPerSecond: 12000000 // Increased bitrate for 720p HQ
            };

            const recorder = new MediaRecorder(stream, options);
            const chunks = [];

            recorder.ondataavailable = (e) => {
                if (e.data.size > 0) chunks.push(e.data);
            };

            recorder.onstop = () => {
                const blob = new Blob(chunks, { type: mimeType });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = filename;
                a.click();
                resolve();
            };

            recorder.start();

            videoRef.current.play().then(() => {
                const durationMs = (end - start) * 1000;
                setTimeout(() => {
                    recorder.stop();
                    videoRef.current.pause();
                }, durationMs);

                const recFrame = () => {
                    if (recorder.state === 'recording') {
                        processFrame();
                        requestAnimationFrame(recFrame);
                    }
                };
                recFrame();
            });
        });
    };

    // Single File Highlight Reel Logic
    const handleHighlightReel = async () => {
        if (shots.length === 0 || !canvasRef.current || !videoRef.current) return;
        setIsExporting(true);
        videoRef.current.pause();

        const stream = canvasRef.current.captureStream(60);
        let mimeType = 'video/mp4; codecs="avc1.42E01E, mp4a.40.2"';
        if (!MediaRecorder.isTypeSupported(mimeType)) {
            mimeType = 'video/webm; codecs=vp9';
        }

        const options = { mimeType, videoBitsPerSecond: 8000000 };
        const recorder = new MediaRecorder(stream, options);
        const chunks = [];

        recorder.ondataavailable = (e) => {
            if (e.data.size > 0) chunks.push(e.data);
        };

        recorder.onstop = () => {
            const blob = new Blob(chunks, { type: mimeType });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `cricketx_highlights_reel_${Date.now()}.${mimeType === 'video/mp4' || mimeType.includes('mp4') ? 'mp4' : 'webm'}`;
            a.click();
            setIsExporting(false);
            setIsPlaying(false);
        };

        recorder.start(); // Start recording

        // Sequencing logic
        for (let i = 0; i < shots.length; i++) {
            const shot = shots[i];
            const start = Math.max(0, shot.timestamp - 1.0);
            const end = Math.min(duration, shot.timestamp + 1.0);
            const durationMs = (end - start) * 1000;

            // Resume if paused (for shots after first)
            if (recorder.state === 'paused') recorder.resume();

            // Seek and Play
            videoRef.current.currentTime = start;
            // Wait briefly for seek to assume frame stability? 
            // Ideally we just play.

            await videoRef.current.play();

            // Record for duration
            const startTime = Date.now();
            while (Date.now() - startTime < durationMs) {
                processFrame(); // Ensure visual updates
                await new Promise(r => requestAnimationFrame(r));
            }

            videoRef.current.pause();
            recorder.pause(); // Pause recording between clips
        }

        recorder.stop();
    };

    const handleExport = (isFull = false) => {
        const start = isFull ? 0 : (trimRange?.start || 0);
        const end = isFull ? duration : (trimRange?.end || duration);

        setIsExporting(true);
        const prefix = isFull ? 'full_analysis' : 'clip';
        downloadShot(start, end, `cricketx_${prefix}_${Date.now()}.mp4`).then(() => {
            setIsExporting(false);
            setIsPlaying(false);
        });
    };

    return (
        <div className="flex flex-col min-h-full bg-black relative">
            {/* Toolbar */}
            <div className="absolute top-4 left-4 z-20">
                <button
                    onClick={() => setShow3D(!show3D)}
                    className={`p-2 rounded-lg flex items-center gap-2 text-sm font-bold ${show3D ? 'bg-neon-cyan text-black' : 'bg-gray-800 text-white'}`}
                >
                    <Box size={16} /> 3D View
                </button>

                {role === 'bowler' && (
                    <div className="flex bg-gray-800 rounded-lg p-1 border border-gray-700">
                        <button
                            onClick={() => {
                                setBowlingArm('left');
                                pathBufferRef.current = [];
                                setArmPath([]);
                            }}
                            className={`px-3 py-1 text-xs font-bold rounded-md transition-colors ${bowlingArm === 'left' ? 'bg-neon-magenta text-white' : 'text-gray-400 hover:text-white'}`}
                        >
                            Left Arm
                        </button>
                        <button
                            onClick={() => {
                                setBowlingArm('right');
                                pathBufferRef.current = [];
                                setArmPath([]);
                            }}
                            className={`px-3 py-1 text-xs font-bold rounded-md transition-colors ${bowlingArm === 'right' ? 'bg-neon-magenta text-white' : 'text-gray-400 hover:text-white'}`}
                        >
                            Right Arm
                        </button>
                    </div>
                )}
            </div>

            <div className={`flex flex-row overflow-hidden h-[500px] md:h-[650px] bg-gray-900 ${show3D ? '' : 'justify-center'}`}>
                {isModelLoading && (
                    <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/80">
                        <div className="text-neon-cyan animate-pulse">Loading Biomechanics Engine...</div>
                    </div>
                )}

                {isExporting && (
                    <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/80">
                        <div className="text-white font-bold animate-pulse">
                            Creating Highlight Reel... {shots.length} clips
                        </div>
                    </div>
                )}

                {/* Video Area */}
                <div className={`relative flex items-center justify-center transition-all duration-300 h-full ${show3D ? 'w-1/2' : 'w-full'}`}>
                    <video
                        ref={videoRef}
                        src={videoSrc}
                        className="absolute inset-0 w-full h-full object-contain pointer-events-none opacity-0"
                        onLoadedMetadata={(e) => {
                            setDuration(e.target.duration);
                            if (canvasRef.current) {
                                canvasRef.current.width = e.target.videoWidth;
                                canvasRef.current.height = e.target.videoHeight;
                            }
                        }}
                        onTimeUpdate={handleTimeUpdate}
                        playsInline
                        muted
                        crossOrigin="anonymous"
                    />
                    <canvas
                        ref={canvasRef}
                        className="w-full h-full object-contain"
                    />

                    {/* Dynamic Filter Bar */}
                    <div className="absolute top-4 right-4 left-44 z-20 flex gap-2 overflow-x-auto no-scrollbar bg-black/60 backdrop-blur-xl rounded-2xl p-2 border border-white/10 items-center shadow-2xl">
                        <div className="flex items-center gap-2 border-r border-white/10 pr-3 mr-1 shrink-0">
                            <button
                                onClick={() => setOverlayConfig(p => ({ ...p, showSkeleton: !p.showSkeleton }))}
                                className={`px-2.5 py-1.5 text-[9px] uppercase tracking-wider font-black rounded-lg transition-all ${overlayConfig.showSkeleton ? 'bg-green-500 text-black shadow-[0_0_10px_rgba(34,197,94,0.4)]' : 'bg-gray-800/50 text-gray-500 border border-white/5'}`}
                            >
                                Skeleton
                            </button>
                            {role === 'bowler' && (
                                <button
                                    onClick={() => setOverlayConfig(p => ({ ...p, showArmPath: !p.showArmPath }))}
                                    className={`px-2.5 py-1.5 text-[9px] uppercase tracking-wider font-black rounded-lg transition-all ${overlayConfig.showArmPath ? 'bg-blue-600 text-white shadow-[0_0_10px_rgba(37,99,235,0.4)]' : 'bg-gray-800/50 text-gray-500 border border-white/5'}`}
                                >
                                    Arm Path
                                </button>
                            )}
                            <button
                                onClick={() => setOverlayConfig(p => ({ ...p, showStats: !p.showStats }))}
                                className={`px-2.5 py-1.5 text-[9px] uppercase tracking-wider font-black rounded-lg transition-all ${overlayConfig.showStats ? 'bg-fuchsia-600 text-white shadow-[0_0_10px_rgba(192,38,211,0.4)]' : 'bg-gray-800/50 text-gray-500 border border-white/5'}`}
                            >
                                Stats
                            </button>
                        </div>

                        <div className="flex gap-2 items-center overflow-x-auto no-scrollbar">
                            {Object.entries(ANGLE_DEFINITIONS).map(([id, def]) => {
                                const isActive = overlayConfig.activeAngles.includes(id);
                                const isExt = overlayConfig.externalAngles[id];
                                return (
                                    <div key={id} className={`flex items-center rounded-lg border transition-all duration-300 ${isActive ? 'bg-gray-800/80 border-white/20' : 'bg-transparent border-white/5'}`}>
                                        <button
                                            onClick={() => {
                                                setOverlayConfig(prev => ({
                                                    ...prev,
                                                    activeAngles: prev.activeAngles.includes(id)
                                                        ? prev.activeAngles.filter(a => a !== id)
                                                        : [...prev.activeAngles, id]
                                                }));
                                            }}
                                            className={`px-3 py-1.5 text-[9px] font-bold rounded-l-lg transition-all whitespace-nowrap ${isActive ? 'text-neon-cyan' : 'text-gray-600 hover:text-gray-400'}`}
                                        >
                                            {def.label}
                                        </button>
                                        {isActive && (
                                            <button
                                                onClick={() => {
                                                    setOverlayConfig(prev => ({
                                                        ...prev,
                                                        externalAngles: { ...prev.externalAngles, [id]: !prev.externalAngles[id] }
                                                    }));
                                                }}
                                                className={`px-2 py-1.5 text-[8px] font-mono font-bold rounded-r-lg border-l border-white/10 hover:bg-white/5 ${isExt ? 'text-red-400 bg-red-400/10' : 'text-green-400 bg-green-400/10'}`}
                                            >
                                                {isExt ? 'EXT' : 'INT'}
                                            </button>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Stats overlay removed from DOM - now rendered in Canvas */}
                </div>

                {/* 3D Area */}
                {show3D && (
                    <div className="w-1/2 h-full relative border-l border-gray-800 flex items-center justify-center bg-gray-900">
                        <Biomechanics3DView landmarks={landmarks3D} role={role} />
                    </div>
                )}
            </div>

            <div className="bg-gray-900 border-t border-gray-800 p-4">
                <div className="mb-4 flex items-center gap-2 text-xs text-gray-400 font-mono relative">
                    <span>{currentTime.toFixed(2)}s</span>
                    <div className="flex-1 relative h-6 flex items-center">
                        <input
                            type="range"
                            min={0}
                            max={duration}
                            step={0.01}
                            value={currentTime}
                            onChange={(e) => {
                                const t = parseFloat(e.target.value);
                                videoRef.current.currentTime = t;
                                setCurrentTime(t);
                            }}
                            disabled={isExporting}
                            className="w-full h-1 bg-gray-700 rounded-lg appearance-none cursor-pointer z-10 relative"
                        />
                        {/* Trim Slider - Start */}
                        {trimRange && (
                            <div
                                className="absolute h-4 w-1 bg-neon-cyan z-20 cursor-ew-resize top-1 translate-y-[-50%]"
                                style={{ left: `${(trimRange.start / duration) * 100}%` }}
                            />
                        )}
                        {/* Trim Slider - End */}
                        {trimRange && (
                            <div
                                className="absolute h-4 w-1 bg-neon-cyan z-20 cursor-ew-resize top-1 translate-y-[-50%]"
                                style={{ left: `${(trimRange.end / duration) * 100}%` }}
                            />
                        )}
                        {/* Markers - Only for Batsman */}
                        {role === 'batsman' && shots.map((shot) => (
                            <div
                                key={shot.id}
                                className="absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-neon-magenta rounded-full pointer-events-none z-20"
                                style={{ left: `${(shot.timestamp / duration) * 100}%` }}
                            />
                        ))}
                    </div>
                    <span>{duration.toFixed(2)}s</span>
                </div>

                {/* Manual Trim Controls */}
                <div className="mb-4 bg-gray-800 p-3 rounded-lg border border-gray-700">
                    <div className="flex justify-between items-center mb-2">
                        <h3 className="text-gray-400 text-xs font-bold uppercase">Manual Trim Range</h3>
                        <div className="flex gap-2">
                            <button
                                onClick={() => setTrimRange(null)}
                                className="text-[10px] text-red-400 hover:text-red-300 uppercase underline"
                            >
                                Reset Trim
                            </button>
                        </div>
                    </div>
                    <div className="flex gap-4 items-center">
                        <div className="flex-1">
                            <label className="text-[10px] text-gray-500 block mb-1">Start: {trimRange?.start.toFixed(2) || "0.00"}s</label>
                            <input
                                type="range"
                                min={0}
                                max={trimRange?.end || duration}
                                step={0.01}
                                value={trimRange?.start || 0}
                                onChange={(e) => {
                                    const val = parseFloat(e.target.value);
                                    setTrimRange(prev => ({ start: val, end: prev?.end || duration }));
                                }}
                                className="w-full h-1 bg-gray-700 rounded-lg appearance-none cursor-pointer"
                            />
                        </div>
                        <div className="flex-1">
                            <label className="text-[10px] text-gray-500 block mb-1">End: {trimRange?.end.toFixed(2) || duration.toFixed(2)}s</label>
                            <input
                                type="range"
                                min={trimRange?.start || 0}
                                max={duration}
                                step={0.01}
                                value={trimRange?.end || duration}
                                onChange={(e) => {
                                    const val = parseFloat(e.target.value);
                                    setTrimRange(prev => ({ start: prev?.start || 0, end: val }));
                                }}
                                className="w-full h-1 bg-gray-700 rounded-lg appearance-none cursor-pointer"
                            />
                        </div>
                    </div>
                </div>

                <div className="flex justify-between items-center">
                    <div className="flex gap-2">
                        <button
                            onClick={togglePlay}
                            disabled={isExporting}
                            className="p-3 bg-gray-700 hover:bg-gray-600 rounded-full text-white transition-colors disabled:opacity-50"
                        >
                            {isPlaying ? <Pause size={20} /> : <Play size={20} />}
                        </button>
                        <button
                            onClick={onRestart}
                            disabled={isExporting}
                            className="p-3 bg-gray-800 hover:bg-gray-700 rounded-full text-gray-400 hover:text-white transition-colors disabled:opacity-50"
                            title="New Analysis"
                        >
                            <RotateCcw size={20} />
                        </button>
                    </div>

                    <div className="flex gap-2">
                        {!keyFrame ? (
                            <button
                                onClick={setKeyFrameCurrent}
                                disabled={isExporting}
                                className="flex items-center gap-2 px-6 py-3 bg-neon-cyan text-black font-bold rounded-lg hover:bg-cyan-400 transition-colors disabled:opacity-50"
                            >
                                <Scissors size={18} />
                                Mark {role === 'bowler' ? 'Release' : 'Contact'}
                            </button>
                        ) : (
                            <>
                                <button
                                    className="flex items-center gap-2 px-4 py-3 bg-gray-700 text-white font-medium rounded-lg hover:bg-gray-600 disabled:opacity-50"
                                    disabled={isExporting}
                                >
                                    <Share2 size={18} /> Share
                                </button>
                                <button
                                    onClick={() => handleExport(false)}
                                    disabled={isExporting}
                                    className="flex items-center gap-2 px-4 py-3 bg-blue-600 text-white font-bold rounded-lg hover:bg-blue-500 disabled:opacity-50"
                                >
                                    <Download size={18} /> {isExporting ? 'Exporting...' : 'Export Trim'}
                                </button>
                                <button
                                    onClick={() => handleExport(true)}
                                    disabled={isExporting}
                                    className="flex items-center gap-2 px-4 py-3 bg-emerald-600 text-white font-bold rounded-lg hover:bg-emerald-500 disabled:opacity-50"
                                >
                                    <Download size={18} /> Download Full
                                </button>
                            </>
                        )}
                    </div>
                </div>

                {/* Analysis Tools: Sensitivity & Graph - Only for Batsman */}
                {role === 'batsman' && (
                    <div className="my-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
                        {/* Controls */}
                        <div className="bg-gray-800 p-3 rounded-lg border border-gray-700 space-y-4">
                            {/* Sensitivity */}
                            <div>
                                <div className="flex justify-between items-center mb-2">
                                    <h3 className="text-gray-400 text-xs font-bold uppercase">Shot Detection Sensitivity</h3>
                                    <span className="text-neon-cyan font-mono font-bold">{sensitivity}</span>
                                </div>
                                <input
                                    type="range"
                                    min="1"
                                    max="50"
                                    value={sensitivity}
                                    onChange={(e) => setSensitivity(Number(e.target.value))}
                                    className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer"
                                />
                                <p className="text-[10px] text-gray-500 mt-1">
                                    Threshold for detection (Logic uses Average Graph).
                                </p>
                            </div>

                            {/* Smoothing Window */}
                            <div className="pt-4 border-t border-gray-700">
                                <div className="flex justify-between items-center mb-2">
                                    <h3 className="text-gray-400 text-xs font-bold uppercase">Smoothing Window</h3>
                                    <span className="text-blue-400 font-mono font-bold">{smoothingWindow}s</span>
                                </div>
                                <input
                                    type="range"
                                    min="0.1"
                                    max="1.0"
                                    step="0.05"
                                    value={smoothingWindow}
                                    onChange={(e) => setSmoothingWindow(Number(e.target.value))}
                                    className="w-full h-2 bg-gray-700 rounded-lg appearance-none cursor-pointer"
                                />
                                <p className="text-[10px] text-gray-500 mt-1">
                                    Duration for calculating average velocity.
                                </p>
                            </div>
                        </div>

                        {/* Graphs Area */}
                        <div className="lg:col-span-2 space-y-2">
                            {/* Instant Graph */}
                            <div className="bg-gray-800 p-2 rounded-lg border border-gray-700 h-24 relative">
                                <Line
                                    data={{
                                        labels: graphData.labels,
                                        datasets: [{
                                            label: 'Instant Speed',
                                            data: graphData.instant,
                                            borderColor: 'rgb(255, 99, 132)',
                                            borderWidth: 1,
                                            pointRadius: 0,
                                            tension: 0.1
                                        }]
                                    }}
                                    options={{
                                        responsive: true,
                                        maintainAspectRatio: false,
                                        animation: false,
                                        scales: {
                                            y: { min: 0, max: 100, ticks: { color: '#6b7280', font: { size: 9 }, maxTicksLimit: 3 }, grid: { display: false } },
                                            x: { display: false }
                                        },
                                        plugins: { legend: { display: false }, title: { display: true, text: 'Instant Velocity', color: '#ef4444', font: { size: 10 }, padding: 2 } }
                                    }}
                                />
                            </div>

                            {/* Average Graph */}
                            <div className="bg-gray-800 p-2 rounded-lg border border-gray-700 h-32 relative">
                                <Line
                                    data={{
                                        labels: graphData.labels,
                                        datasets: [
                                            {
                                                label: 'Average Speed',
                                                data: graphData.average,
                                                borderColor: 'rgb(59, 130, 246)',
                                                borderWidth: 2,
                                                pointRadius: 0,
                                                tension: 0.4
                                            },
                                            {
                                                label: 'Threshold',
                                                data: graphData.threshold,
                                                borderColor: 'rgb(34, 197, 94)',
                                                borderWidth: 1,
                                                borderDash: [5, 5],
                                                pointRadius: 0
                                            }
                                        ]
                                    }}
                                    options={{
                                        responsive: true,
                                        maintainAspectRatio: false,
                                        animation: false,
                                        scales: {
                                            y: { min: 0, max: 100, ticks: { color: '#9ca3af', font: { size: 10 } }, grid: { color: 'rgba(255,255,255,0.05)' } },
                                            x: { display: false }
                                        },
                                        plugins: {
                                            legend: { display: true, labels: { color: '#9ca3af', boxWidth: 8, font: { size: 9 } } },
                                            title: { display: true, text: 'Smoothed Average Velocity', color: '#3b82f6', font: { size: 10 }, padding: 2 }
                                        }
                                    }}
                                />
                            </div>
                        </div>
                    </div>
                )}

                {/* Shot List & Highlights - Only for Batsman */}
                {role === 'batsman' && shots.length > 0 && (
                    <div className="mt-2 p-3 bg-gray-800 rounded-lg border border-gray-700">
                        <div className="flex justify-between items-center mb-2">
                            <h3 className="text-neon-cyan font-bold text-sm">Detected Shots ({shots.length})</h3>
                            <button
                                onClick={handleHighlightReel}
                                disabled={isExporting}
                                className="px-3 py-1 bg-neon-magenta text-black font-bold text-xs rounded hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
                            >
                                <Download size={14} /> Download Highlight Reel (Merged)
                            </button>
                        </div>

                        <div className="max-h-52 overflow-y-auto pr-2 custom-scrollbar">
                            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-2">
                                {shots.map((shot, idx) => (
                                    <div key={shot.id} className="bg-gray-700/50 p-2 rounded border border-gray-600 hover:border-gray-500 transition-colors">
                                        <div className="flex justify-between items-center mb-1">
                                            <span className="text-white font-mono font-bold text-xs">#{idx + 1}</span>
                                            <span className="text-gray-400 text-[10px] font-mono">{shot.timestamp.toFixed(1)}s</span>
                                        </div>

                                        <div className="flex justify-between items-center bg-black/20 p-1.5 rounded mb-2">
                                            <span className="text-[10px] text-gray-400 uppercase">Score</span>
                                            <div className="text-neon-cyan font-mono font-bold text-sm">{shot.score.toFixed(1)}</div>
                                        </div>

                                        <div className="flex gap-1">
                                            <button
                                                onClick={() => handleViewShot(shot)}
                                                className="flex-1 bg-neon-cyan/20 hover:bg-neon-cyan/40 text-neon-cyan py-1 rounded text-[10px] font-bold flex items-center justify-center gap-1 transition-colors"
                                            >
                                                <Play size={10} /> View
                                            </button>
                                            <button
                                                onClick={() => {
                                                    const start = Math.max(0, shot.timestamp - 1.0);
                                                    const end = Math.min(duration, shot.timestamp + 1.0);
                                                    downloadShot(start, end, `cricketx_shot_${idx + 1}.mp4`);
                                                }}
                                                disabled={isExporting}
                                                className="flex-1 bg-gray-600 hover:bg-gray-500 text-white py-1 rounded text-[10px] font-bold flex items-center justify-center gap-1 transition-colors disabled:opacity-50"
                                            >
                                                <Download size={10} /> Save
                                            </button>
                                            <button
                                                onClick={() => setShots(s => s.filter(i => i.id !== shot.id))}
                                                className="px-1.5 bg-red-500/10 hover:bg-red-500/30 text-red-400 rounded transition-colors"
                                                title="Delete"
                                            >
                                                ×
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PoseAnalyzer;
